import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import {
  Badge,
  Card,
  ErrorState,
  Loading,
  PageHeader,
  Select,
  StatCard,
} from '@/components/ui';
import { formatDateTime } from '@/lib/format';
import SttHealth from './SttHealth';
import CallArrivals from './CallArrivals';
import AsteriskChannels from './AsteriskChannels';
import { useState } from 'react';

/**
 * Why did that call fail.
 *
 * Everything needed to answer it already existed and was scattered: the call
 * row knew it ended badly, the provider table knew what was configured, the
 * vendor knew the balance was gone, and nothing put the three together. So a
 * failed call meant SSH and journalctl.
 */

interface Quota {
  unit: string;
  used: number;
  limit: number;
  remaining: number;
  calls_remaining: number;
  tier: string;
  exhausted: boolean;
}

interface Provider {
  id: string;
  kind: string;
  name: string;
  driver: string;
  enabled: boolean;
  is_default: boolean;
  health_state: string | null;
  has_credentials: boolean;
  usable: boolean;
  mocked: boolean;
  quota: Quota | null;
  config: Record<string, unknown> | null;
}

interface Effective {
  name: string | null;
  driver: string | null;
  model: string | null;
  mocked: boolean;
}

interface FailedCall {
  id: string;
  client_name: string | null;
  caller_number: string;
  state: string;
  started_at: string;
  duration_sec: number | null;
  bot_name: string | null;
  bot_turns: number;
  customer_turns: number;
  diagnosis: string;
  detail: string;
}

const DIAGNOSIS_TONE: Record<string, 'red' | 'amber' | 'gray'> = {
  'transfer failed': 'red',
  'transfer never completed': 'red',
  'the bot never spoke': 'red',
  'the bot heard nothing': 'amber',
  'ended almost immediately': 'gray',
};

/**
 * The waits a caller experiences, the split inside synthesis, and the state of
 * the box they were all measured on -- which is the one that decides whether
 * the others mean anything.
 */
const STAGE_LABELS: [string, string][] = [
  ['tts_ms', 'Voice synthesis'],
  ['llm_ms', 'Model turn'],
  ['http_ms', '— vendor time'],
  ['local_ms', '— local work'],
  ['loop_lag_ms', 'This box, behind by'],
];

export default function Diagnostics() {
  const [hours, setHours] = useState(24);

  const health = useQuery({
    queryKey: ['diagnostics'],
    queryFn: () =>
      api.get<{
        providers: Provider[];
        effective: Record<string, Effective>;
        all_live: boolean;
        log_sink: { running: boolean; queued: number; dropped: number };
      }>('/admin/diagnostics'),
    refetchInterval: 30_000,
  });

  // "The bot is slow to answer" is made of three unrelated waits with three
  // unrelated fixes. Read back out of the system log so the next change is
  // aimed at whichever one is actually costing the time.
  const latency = useQuery({
    queryKey: ['turn-latency'],
    queryFn: () =>
      api.get<{
        stages: Record<string, { samples: number; p50: number; p95: number; worst: number } | null>;
        advice: string[];
      }>('/admin/turn-latency'),
    refetchInterval: 60_000,
  });

  const failures = useQuery({
    queryKey: ['failed-calls', hours],
    queryFn: () =>
      api.get<{ data: FailedCall[]; examined: number }>('/admin/failed-calls', { hours }),
    refetchInterval: 30_000,
  });

  if (health.isPending) return <Loading />;
  if (health.error) return <ErrorState error={health.error} retry={() => health.refetch()} />;

  const providers = health.data?.providers ?? [];
  const effective = health.data?.effective ?? {};
  const sink = health.data?.log_sink;
  // How calls are starting, which is the half of 'why did that call fail'
  // that happens before the bot exists.
  const diag = health.data as any;

  // The things that will break the next call, said first and plainly.
  const alerts: string[] = [];
  for (const provider of providers) {
    if (provider.quota?.exhausted) {
      alerts.push(`${provider.name} has no ${provider.quota.unit} left. Calls will be silent.`);
    } else if (provider.quota && provider.quota.calls_remaining < 100) {
      alerts.push(
        `${provider.name} has about ${provider.quota.calls_remaining} calls of balance left.`,
      );
    }
    if (provider.enabled && !provider.usable) {
      alerts.push(`${provider.name} is enabled but has no credentials, so calls fall back to the mock.`);
    }
  }
  for (const [kind, row] of Object.entries(effective)) {
    if (row.mocked) alerts.push(`${kind} is in MOCK mode — no real provider is being called.`);
    if (!row.name && !row.mocked) alerts.push(`No usable ${kind} provider is configured.`);
  }
  if (sink && !sink.running) {
    alerts.push('The system log is not being written, so the System log page will stay empty.');
  }
  if (sink && sink.dropped > 0) {
    alerts.push(`${sink.dropped} log line(s) were dropped under load, so the log has gaps.`);
  }

  return (
    <div className="w-full">
      <PageHeader
        title="Diagnostics"
        description="What would happen on the next call, and what went wrong on the last ones."
      />

      {alerts.length > 0 ? (
        <Card title="Needs attention">
          <ul className="space-y-1.5 text-sm">
            {alerts.map((alert) => (
              <li key={alert} className="flex gap-2">
                <span className="text-red-600 dark:text-red-400">●</span>
                <span>{alert}</span>
              </li>
            ))}
          </ul>
        </Card>
      ) : (
        <Card title="Needs attention">
          <p className="text-sm text-ink-600 dark:text-ink-300">
            Nothing to report. Every enabled provider has credentials, none is out of
            balance, and the system log is being written.
          </p>
        </Card>
      )}

      <div className="mt-4">
        <SttHealth hours={hours} />
      </div>

      {/* Directly under the call-start funnel, because the funnel says how
          many arrived and this says which ones and what happened to each. */}
      <div className="mt-4">
        <CallArrivals hours={hours} />
      </div>

      {/* Underneath the arrivals, because it answers the question the arrivals
          board raises: if the dialer is busy and nothing arrived, where did
          those calls go? */}
      <div className="mt-4">
        <AsteriskChannels />
      </div>

      <div className="mt-4">
        <Card
          title="What a call would use right now"
          description="Configuration says what is intended. This says what would actually happen."
        >
          <div className="grid gap-3 sm:grid-cols-3">
            {Object.entries(effective).map(([kind, row]) => (
              <div
                key={kind}
                className="rounded-md border border-line p-3"
              >
                <div className="text-xs uppercase tracking-wide text-ink-500">{kind}</div>
                <div className="mt-1 text-sm font-medium">
                  {row.mocked ? 'Mock' : (row.name ?? 'none configured')}
                </div>
                <div className="text-xs text-ink-500 dark:text-ink-400">
                  {row.driver ?? '—'}
                  {row.model ? ` · ${row.model}` : ''}
                </div>
                {row.mocked && (
                  <Badge tone="amber" className="mt-2">
                    mock mode
                  </Badge>
                )}
              </div>
            ))}
          </div>
        </Card>
      </div>

      <div className="mt-4">
        {diag.call_start && diag.call_start.arrived > 0 && (
          <Card
            title="How calls are starting"
            description={`The last ${diag.window_hours ?? 24} hours. Before the bot says anything.`}
          >
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              <StatCard
                label="Dials arrived"
                value={String(diag.call_start.arrived)}
                dense
              />
              <StatCard
                label="Reached the bot"
                value={
                  diag.call_start.reached_the_bot_pct === null
                    ? '—'
                    : `${diag.call_start.reached_the_bot_pct}%`
                }
                sub={`${diag.call_start.started} of ${diag.call_start.arrived}`}
                tone={
                  (diag.call_start.reached_the_bot_pct ?? 100) < 90 ? 'bad' : 'good'
                }
                dense
              />
              <StatCard
                label="Never heard"
                value={String(diag.call_start.never_heard)}
                sub="media path never opened"
                tone={diag.call_start.never_heard ? 'warn' : 'default'}
                dense
              />
              <StatCard
                label="Media retries"
                value={String(diag.call_start.media_failures)}
                dense
              />
            </div>

            {(diag.call_start.reached_the_bot_pct ?? 100) < 90 && (
              <p className="mt-3 rounded-md bg-amber-50 p-3 text-xs text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
                <strong>
                  {(100 - (diag.call_start.reached_the_bot_pct ?? 100)).toFixed(1)}% of
                  dials never reached the bot.
                </strong>{' '}
                The media path could not be opened, which usually means the
                channel was already gone when the call arrived — the dialer
                abandoned or queued it. These still count as calls everywhere
                else on this platform, so campaign totals and transfer rates are
                being measured against a larger number than actually happened.
                Look at the dialer&apos;s own timeout and queue settings first.
              </p>
            )}
          </Card>
        )}

        <Card
          title="How long the bot takes to answer"
          description="Recorded only when a stage passes 2.5 seconds, so an empty table means nothing was slow."
        >
          <div className="grid gap-3 sm:grid-cols-4">
            {STAGE_LABELS.map(([key, label]) => {
              const stage = latency.data?.stages?.[key];
              return (
                <div
                  key={key}
                  className="rounded-md border border-line p-3"
                >
                  <div className="text-xs uppercase tracking-wide text-ink-500">
                    {label}
                  </div>
                  {stage ? (
                    <>
                      <div className="mt-1 text-lg font-semibold tabular-nums">
                        {(stage.p50 / 1000).toFixed(1)}s
                      </div>
                      <div className="text-xs text-ink-500 dark:text-ink-400">
                        p95 {(stage.p95 / 1000).toFixed(1)}s · {stage.samples} samples
                      </div>
                    </>
                  ) : (
                    <div className="mt-1 text-sm text-ink-400">nothing slow</div>
                  )}
                </div>
              );
            })}
          </div>

          {(latency.data?.advice ?? []).length > 0 && (
            <ul className="mt-3 space-y-1.5 text-xs text-ink-600 dark:text-ink-300">
              {latency.data?.advice.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <div className="mt-4">
        <Card title="Providers and balance">
          <div className="overflow-x-auto">
            <table className="glee-table glee-table--compact">
              <thead>
                <tr>
                  <th>Provider</th>
                  <th>Kind</th>
                  <th>Driver</th>
                  <th>Credentials</th>
                  <th>Balance</th>
                </tr>
              </thead>
              <tbody>
                {providers.map((provider) => (
                  <tr key={provider.id}>
                    <td>
                      {provider.name}
                      {provider.is_default && (
                        <Badge tone="blue" className="ml-2">
                          default
                        </Badge>
                      )}
                    </td>
                    <td>{provider.kind}</td>
                    <td className="font-mono text-xs">{provider.driver}</td>
                    <td>
                      {provider.usable ? (
                        <Badge tone="green">ok</Badge>
                      ) : (
                        <Badge tone="red">missing</Badge>
                      )}
                    </td>
                    <td>
                      {provider.quota ? (
                        <span className={provider.quota.exhausted ? 'text-red-600' : ''}>
                          {provider.quota.remaining.toLocaleString()} {provider.quota.unit} left
                          {' · '}
                          <span className="text-ink-500">
                            ~{provider.quota.calls_remaining.toLocaleString()} calls
                          </span>
                        </span>
                      ) : (
                        <span className="text-ink-400">not reported</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      <div className="mt-4">
        <Card
          title="Calls worth looking at"
          description="Not only errors. A call that greeted, heard nothing and ended is not an error anywhere, and is the expensive one."
        >
          <div className="mb-3">
            <Select value={String(hours)} onChange={(e) => setHours(Number(e.target.value))}>
              <option value="1">Last hour</option>
              <option value="24">Last 24 hours</option>
              <option value="168">Last 7 days</option>
              <option value="720">Last 30 days</option>
            </Select>
          </div>

          {failures.isPending && <Loading />}
          {failures.data && failures.data.data.length === 0 && (
            <p className="text-sm text-ink-600 dark:text-ink-300">
              Nothing wrong in {failures.data.examined} call(s) over this window.
            </p>
          )}
          {failures.data && failures.data.data.length > 0 && (
            <div className="overflow-x-auto">
              <table className="glee-table glee-table--compact">
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Number</th>
                    <th>Bot</th>
                    <th>Turns</th>
                    <th>What went wrong</th>
                  </tr>
                </thead>
                <tbody>
                  {failures.data.data.map((call) => (
                    <tr key={call.id}>
                      <td className="whitespace-nowrap">
                        <Link className="text-blue-600 hover:underline" to={`/admin/calls/${call.id}`}>
                          {formatDateTime(call.started_at)}
                        </Link>
                      </td>
                      <td className="whitespace-nowrap">{call.caller_number}</td>
                      <td>{call.bot_name ?? '—'}</td>
                      <td className="whitespace-nowrap text-xs text-ink-500">
                        {call.bot_turns} bot / {call.customer_turns} caller
                      </td>
                      <td>
                        <Badge tone={DIAGNOSIS_TONE[call.diagnosis] ?? 'gray'}>
                          {call.diagnosis}
                        </Badge>
                        <div className="mt-1 text-xs text-ink-500 dark:text-ink-400">
                          {call.detail}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
